import type { LinkSegments, LocalizedLinkSegments } from '$lib/components/seo/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { SupportedLocale } from '@viamichelin/business';
import type { ParamsWithCategory } from '../../params/poiCategory';
import {
	type PoiCategory,
	SEOPoiCategories,
	type SEOPoiCategory,
} from '$lib/components/pois/types';
import { isVanitySearchParams } from '$lib/services/urls/vanity';

// TODO: on itinerary search page, searchparams itinerary=&withCaravan=false&zoiSettings=false~20 are useless

export function isSEOPoiCategory(category: PoiCategory): category is SEOPoiCategory {
	return SEOPoiCategories.includes(category as SEOPoiCategory);
}

export function pageIsSEOIndexable(
	url: URL,
	params: ParamsWithCategory,
	hasRelatedCanonicalUrl: boolean,
) {
	return hasRelatedCanonicalUrl || pageIsCanonicalUrl(url, params);
}

export function getPaginationData(page: number, pageCount: number, allowIndex: boolean) {
	return allowIndex ? { currentPage: page ?? 1, pageCount } : undefined;
}

export function pageIsCanonicalUrl(url: URL, params: ParamsWithCategory) {
	return (
		isVanitySearchParams(url)
		&& (!params.category || (params.category && isSEOPoiCategory(params.category)))
	);
}

interface GetBreadcrumbsArgs {
	params: ParamsWithCategory;
	segments: LinkSegments;
	locale: SupportedLocale;
	localizedLinkSegments?: LocalizedLinkSegments;
	searchParams: SearchParams;
}

export function getBreadcrumbs({
	params,
	searchParams,
	segments,
	locale,
	localizedLinkSegments,
}: GetBreadcrumbsArgs) {
	let showBreadcrumbs = true;

	if (params.category) {
		showBreadcrumbs = isSEOPoiCategory(params.category);
	} else {
		if (
			Array.isArray(searchParams.poiCategories)
			&& searchParams.poiCategories.length > 0
		) {
			showBreadcrumbs = searchParams.poiCategories.every(isSEOPoiCategory);
		}
	}

	return showBreadcrumbs ? [...segments, ...(localizedLinkSegments?.[locale] ?? [])] : [];
}

export function getPageUrl(url: URL, page: number) {
	const baseUrl = new URL(url);

	if (page > 1) {
		baseUrl.searchParams.set('page', String(page));
	} else {
		baseUrl.searchParams.delete('page');
	}

	return baseUrl;
}

export function buildPrevNextLinks(url: URL, currentPage: number, pageCount: number) {
	const baseUrl = new URL(url);
	const prevLink = currentPage > 1 ? getPageUrl(baseUrl, currentPage - 1).toString() : undefined;
	const nextLink = currentPage < pageCount ? getPageUrl(baseUrl, currentPage + 1).toString() : undefined;

	return { prevLink, nextLink };
}
